import { useIntl } from 'react-intl';
import { ButtonUI, Tag } from 'sonata-react-components';

import { COUNT_STATUS_ICON, GROUP_CHANGES_COUNT_MESSAGE } from './saveProgressTooltipContent.messages';

import type { SaveStepStatus } from '@/types/saveSteps.types';

export function CountLines({
  countLines,
  status
}: {
  countLines: { id: string; text: string }[];
  status: SaveStepStatus;
}) {
  if (countLines.length === 0) {
    return null;
  }
  return (
    <ul className="saveStepCounts">
      {countLines.map(line => (
        <li className={`saveStepCount ${status}`} key={line.id}>
          {COUNT_STATUS_ICON[status] && (
            <i aria-hidden="true" className={`saveStepCountIcon ${status} ${COUNT_STATUS_ICON[status]}`} />
          )}
          {line.text}
        </li>
      ))}
    </ul>
  );
}

// Shared header row for every group/subgroup: icon + label on the left,
// optional count badge + expand chevron on the right. Only groups with
// something to reveal get `onToggleOpen` (Main Settings and Context &
// Targeting never do — no itemized breakdown exists for them); when a count
// is shown without a chevron, a same-sized spacer takes its place so every
// row's count Tag lines up in the same column. `icon` is optional: the
// Locations/Screens subgroups (venues/attributionZones/screens) render
// label + count only, no icon.
export function SaveGroupHeader({
  countTotal,
  icon,
  isOpen,
  isPreview,
  label,
  onToggleOpen,
  status
}: {
  countTotal?: number;
  icon?: string;
  isOpen?: boolean;
  isPreview?: boolean;
  label: string;
  onToggleOpen?: () => void;
  status: SaveStepStatus;
}) {
  const { formatMessage } = useIntl();

  return (
    <div
      className="saveGroupHeader"
      {...(onToggleOpen && {
        onClick: onToggleOpen,
        role: 'button',
        tabIndex: 0
      })}
    >
      <div className="saveGroupHeaderLeft">
        {icon && (
          <i aria-hidden="true" className={`saveStepIcon ${status} ${icon} ${isPreview ? '' : 'saving'}`.trim()} />
        )}
        <span className={`saveStepLabel ${status}`}>
          {label}
          <span aria-hidden="true" className="saveStepEllipsis">
            ...
          </span>
        </span>
      </div>
      {(typeof countTotal === 'number' || onToggleOpen) && (
        <div className="saveGroupHeaderRight">
          {typeof countTotal === 'number' && (
            <Tag id={`saveGroupCount-${label.replace(/\s+/g, '-').toLowerCase()}`} status="information" tiny>
              {formatMessage(GROUP_CHANGES_COUNT_MESSAGE, { count: countTotal })}
            </Tag>
          )}
          {onToggleOpen ? (
            <ButtonUI
              icon={`fa-solid fa-chevron-${isOpen ? 'up' : 'down'}`}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onToggleOpen();
              }}
              shape="squareRounded"
              size="ultraSmall"
              type="tertiary"
            />
          ) : (
            typeof countTotal === 'number' && <span aria-hidden="true" className="saveGroupHeaderChevronSpacer" />
          )}
        </div>
      )}
    </div>
  );
}
